import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ProviderType } from 'shared/types/ObjectTypes';

type WebhookApiPushModalProps = {
  providerTypes: ProviderType[];
  selectedType: string | null;
  onSelectProviderType: (pt: ProviderType) => void;
  setSelectedType: (id: string | null) => void;
};

const WebhookApiPushModal: React.FC<WebhookApiPushModalProps> = ({
  providerTypes,
  selectedType,
  onSelectProviderType,
  setSelectedType,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const specialProviderTypes = Object.fromEntries(
    providerTypes
      .filter(
        (pt) => pt.value === 'generic_webhooks' || pt.value === 'api_push',
      )
      .map((pt) => [pt.value, pt]),
  );

  const isProviderSelected = (providerValue: string): boolean => {
    return !!(specialProviderTypes[providerValue]?.id === selectedType);
  };

  const handleSelectProviderType = (providerValue: string) => {
    const provider = specialProviderTypes[providerValue];
    if (provider) {
      onSelectProviderType(provider);
      setSelectedType(provider.id);
      navigate(`/routes/new/configureprovider/${provider.value}`);
    }
  };

  return (
    // No character here: the empty-results card above this one shows it.
    <div className="flex flex-col items-center gap-3 text-center">
      <div className="flex flex-col">
        <p className="text-heading-md font-bold text-ui-text-primary-text">
          {t("Not finding what you're looking for?")}
        </p>
        <p className="text-body-md font-normal text-ui-cta-primary">
          {t('We offer APIs and custom webhooks')}
        </p>
      </div>
      {/* An inline grid of two equal columns: both buttons take the width
                    of the wider label, so "Custom Webhook" and "Connect API" are
                    the same size in every language. */}
      <div className="inline-grid grid-cols-2 gap-4">
        <div
          className={`flex h-9 w-full min-w-[120px] cursor-pointer items-center justify-center gap-2 rounded bg-white px-6 py-2 transition-colors duration-300 hover:bg-teal-t10 ${isProviderSelected('generic_webhooks') ? 'border-2 border-brand-accent-green' : 'border border-teal-t100'}`}
          onClick={() => handleSelectProviderType('generic_webhooks')}
          tabIndex={0}
          role="button"
          aria-label={t('Select Custom Webhook')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleSelectProviderType('generic_webhooks');
            }
          }}
        >
          <span className="text-body-md font-bold text-ui-cta-primary">
            {t('Custom Webhook')}
          </span>
        </div>
        <div
          className={`flex h-9 w-full min-w-[120px] cursor-pointer items-center justify-center gap-2 rounded bg-white px-6 py-2 transition-colors duration-300 hover:bg-teal-t10 ${isProviderSelected('api_push') ? 'border-2 border-brand-accent-green' : 'border border-teal-t100'}`}
          onClick={() => handleSelectProviderType('api_push')}
          tabIndex={0}
          role="button"
          aria-label={t('Select API Push')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleSelectProviderType('api_push');
            }
          }}
        >
          <span className="text-body-md font-bold text-ui-cta-primary">
            {t('Connect API')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default WebhookApiPushModal;
